import * as djangioActionCreators from "shared/djangio/action-creators"
import * as actionTypes from "shared/profiles/issueAdder/action-types"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"

import { inIssueFilter, notInIssueFilter } from "shared/search/containers/Filter/FilterCreators"

import swal from "sweetalert"
import swalConfigs from "app/static/frontend/swalConfigs"

import { issueAdderExtraFields } from "shared/profiles/constants/constants"

/**
 * Removes a related resource from the corresponding m2m in the issue and all it's descendants
 * Adds the related resource to the issue's parent, if it has one
 *
 * @param {QuorumDataType instance} qdt: representing the type of related resource
 * @param {String/Number} id: the id for the related resource
 * @param {Object} issue: the issue this resource should no longer be associated with
 * @param {Object, optional} overwriteActionTypes: if there are other actionTypes you want to use for this action
 * @returns {Promise}: serialized issue
 */
export const removeFromIssueAndMoveUp =
    ({ qdt, id, issue, overwriteActionTypes = undefined, region = null }) =>
    async (dispatch, getState) => {
        // do not show the pop up confirmation if the issue has no descendants
        const confirmation =
            issue[issueAdderExtraFields.isLeaf] || (await swal(swalConfigs.REMOVE_ISSUE_AND_SUBISSUES(qdt, issue.name)))

        if (!confirmation) {
            return
        }

        const payload = {
            issue,
            profileId: id,
            dataType: qdt.value,
        }

        const ref = String(qdt.value) + "-" + id
        const issues = getState().issueAdder.getIn([ref, "issues"])

        // Calculate the issues that will be removed and attach them to the payload
        // The issues to remove are issues that contain the target issue in their ancestors
        payload.removedIssueIds = issues.reduce((accSet, iss) => {
            const ancestorIds = (iss.get("_extra") && iss.getIn(["_extra", "ancestor_ids"])) || iss.get("ancestor_ids")

            return ancestorIds.some((ancestorId) => ancestorId === issue.id) ? accSet.add(iss.get("id")) : accSet
        }, new Set())

        const response = dispatch(
            djangioActionCreators.sendAction(
                DjangIO.app.userdata.models.IssueManagement.objects.get(issue.id),
                overwriteActionTypes || actionTypes.removeFromIssue,
                {
                    kwargs: {
                        id,
                        data_type: qdt.value,
                    },
                    action: "move_related_to_parent",
                    payload,
                    cacheInvalidationParams: {
                        endpoints: [DjangIO.newResourceFromDataType(qdt.value).endpoint],
                        shouldClearFn: (url) => {
                            const clearableKeys = inIssueFilter.defaultProps.allFilterKeys.concat(
                                notInIssueFilter.defaultProps.allFilterKeys,
                            )

                            clearableKeys.push("for_project_and_subprojects")

                            return clearableKeys.some((filterKey) => url.includes(filterKey))
                        },
                    },
                },
            ),
        )

        if (response) {
            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.remove_from_issue,
                description: `${qdt.label} removed from issue: ${issue.name}`,
                quorumDataType: qdt.value,
                regions: [region],
            })
        }
    }
